import { useMemo, useState } from 'react';
import { getTransactionQueue } from '@/api/audit/velocity-feed-audit';
import { Button } from '@/components/ui/button';
import { TablePagination } from '@/components/ui/custom/table-pagination';
import { DateRangePickerSplit } from '@/components/ui/date-range-picker-split';
import { SearchBar } from '@/components/ui/SearchBar/SearchBar';
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from '@/components/ui/table';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import {
	flexRender,
	getCoreRowModel,
	getFilteredRowModel,
	getPaginationRowModel,
	getSortedRowModel,
	PaginationState,
	SortingState,
	useReactTable,
} from '@tanstack/react-table';
import { Loader2 } from 'lucide-react';

import { transactionQueueColumns } from './transaction-queue-columns';

export function TransactionQueueTable() {
	const [searchTerm, setSearchTerm] = useState('');
	const [startDate, setStartDate] = useState<Date | null>(null);
	const [endDate, setEndDate] = useState<Date | null>(null);
	const [pagination, setPagination] = useState<PaginationState>({
		pageIndex: 0,
		pageSize: 10,
	});
	const [sorting, setSorting] = useState<SortingState>([]);

	const { data, isFetching, isLoading } = useQuery({
		queryKey: ['velocity-feed-transaction-queue', searchTerm],
		queryFn: () => getTransactionQueue(searchTerm),
		placeholderData: keepPreviousData,
		staleTime: 30_000,
	});

	const transactionQueueData = useMemo(() => data?.data ?? [], [data?.data]);

	const filteredData = useMemo(
		() =>
			transactionQueueData.filter((item) => {
				if (!startDate && !endDate) return true;
				if (!item.effectiveDate) return false;

				const effectiveDate = new Date(item.effectiveDate);
				const effectiveDay = new Date(
					effectiveDate.getFullYear(),
					effectiveDate.getMonth(),
					effectiveDate.getDate(),
				);
				const startDay = startDate
					? new Date(
							startDate.getFullYear(),
							startDate.getMonth(),
							startDate.getDate(),
						)
					: null;
				const endDay = endDate
					? new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate())
					: null;

				return (
					(!startDay || effectiveDay >= startDay) &&
					(!endDay || effectiveDay <= endDay)
				);
			}),
		[transactionQueueData, startDate, endDate],
	);

	const table = useReactTable({
		data: filteredData,
		columns: transactionQueueColumns,
		rowCount: filteredData.length,
		manualPagination: false,
		manualSorting: false,
		onPaginationChange: setPagination,
		onSortingChange: setSorting,
		getCoreRowModel: getCoreRowModel(),
		getPaginationRowModel: getPaginationRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getFilteredRowModel: getFilteredRowModel(),
		state: {
			pagination,
			sorting,
		},
	});

	return (
		<div className="h-1/2 w-full">
			<div className="mb-4 flex flex-col gap-4 md:flex-row md:items-center">
				<SearchBar
					placeholder="Search Transaction Queue..."
					onSearch={(value) => {
						setSearchTerm(value);
						setPagination((prev) => ({ ...prev, pageIndex: 0 }));
					}}
					isLoading={isFetching || isLoading}
					className="w-80"
				/>
				<div className="flex items-center gap-2">
					<DateRangePickerSplit
						startDate={startDate}
						endDate={endDate}
						onChange={(start, end) => {
							setStartDate(start);
							setEndDate(end);
							setPagination((prev) => ({ ...prev, pageIndex: 0 }));
						}}
					/>
					<Button
						variant="outline"
						onClick={() => {
							setStartDate(null);
							setEndDate(null);
							setPagination((prev) => ({ ...prev, pageIndex: 0 }));
						}}
						disabled={!startDate && !endDate}
					>
						Clear Date
					</Button>
				</div>
			</div>

			<div className="table-container relative rounded-md border">
				{isFetching && (
					<div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60 backdrop-blur-sm">
						<Loader2 className="h-6 w-6 animate-spin text-gray-600" />
					</div>
				)}
				<div className="rounded-md border">
					<Table>
						<TableHeader>
							{table.getHeaderGroups().map((headerGroup) => (
								<TableRow key={headerGroup.id}>
									{headerGroup.headers.map((header) => (
										<TableHead key={header.id}>
											{header.isPlaceholder
												? null
												: flexRender(header.column.columnDef.header, header.getContext())}
										</TableHead>
									))}
								</TableRow>
							))}
						</TableHeader>
						<TableBody>
							{table.getRowModel().rows.length ? (
								table.getRowModel().rows.map((row) => (
									<TableRow key={row.id}>
										{row.getVisibleCells().map((cell) => (
											<TableCell key={cell.id}>
												{flexRender(cell.column.columnDef.cell, cell.getContext())}
											</TableCell>
										))}
									</TableRow>
								))
							) : (
								<TableRow>
									<TableCell
										colSpan={table.getAllColumns().length}
										className="py-10 text-center"
									>
										No results.
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</div>
			</div>

			<TablePagination table={table} isFetching={isFetching} />
		</div>
	);
}
